import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { countryFromPhone } from '@/utils/country';
import {
  UtensilsCrossed, Users, Settings, MessageSquare, LogOut, BarChart3,
  Send, Eye, EyeOff, Store, LayoutGrid, Bell, ChevronRight, ChevronDown, Upload,
  Megaphone, Lightbulb, AlertTriangle, Info, ExternalLink, Edit2, Trash2, Image as ImageIcon, Plus, Globe
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/clients`, { withCredentials: true });
      setClients(data);
    } catch {}
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/site-settings`, { withCredentials: true });
      setSettings(data);
    } catch {}
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/messages`, { withCredentials: true });
      setMessages(data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchClients();
    fetchSettings();
    fetchMessages();
  }, [fetchClients, fetchSettings, fetchMessages]);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-stone-50 flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside data-testid="admin-sidebar" className="w-full md:w-64 bg-white border-r border-stone-200 flex-shrink-0 flex flex-col">
        <div className="p-5 border-b border-stone-200">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-stone-900 rounded-lg flex items-center justify-center">
              <UtensilsCrossed className="text-white" size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>CallButton</p>
              <p className="text-xs text-stone-500">Admin Panel</p>
            </div>
          </div>
        </div>
        <nav className="p-3 space-y-1 flex-1">
          {[
            { id: 'clients', icon: Users, label: 'Clients', badge: clients.length },
            { id: 'packages', icon: Store, label: 'Packages' },
            { id: 'site-settings', icon: Settings, label: 'Site Settings' },
            { id: 'customers', icon: Globe, label: 'Customers' },
            { id: 'how-it-works', icon: Lightbulb, label: 'How It Works' },
            { id: 'messages', icon: Megaphone, label: 'Messages', badge: messages.length },
          ].map(item => (
            <button key={item.id} data-testid={`admin-tab-${item.id}`} onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === item.id ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-100'
              }`}>
              <item.icon size={18} />
              <span className="flex-1 text-left">{item.label}</span>
              {item.badge > 0 && <Badge className={activeTab === item.id ? 'bg-white text-stone-900 text-xs' : 'bg-stone-200 text-stone-700 text-xs'}>{item.badge}</Badge>}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-stone-200">
          <div className="px-3 py-2 mb-2">
            <p className="text-xs text-stone-500">{user?.email}</p>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-stone-500 hover:text-red-600 hover:bg-red-50 transition-all">
            <LogOut size={18} /><span>Sign Out</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 p-4 md:p-8 overflow-y-auto">
        {activeTab === 'clients' && <ClientsPanel clients={clients} fetchClients={fetchClients} settings={settings} />}
        {activeTab === 'packages' && <PackagesPanel settings={settings} setSettings={setSettings} />}
        {activeTab === 'site-settings' && <SiteSettingsPanel settings={settings} setSettings={setSettings} />}
        {activeTab === 'customers' && <CustomersPanel settings={settings} setSettings={setSettings} />}
        {activeTab === 'how-it-works' && <HowItWorksPanel settings={settings} setSettings={setSettings} />}
        {activeTab === 'messages' && <MessagesPanel messages={messages} clients={clients} fetchMessages={fetchMessages} />}
      </main>
    </div>
  );
}

// ==================== CLIENTS PANEL ====================
function ClientsPanel({ clients, fetchClients, settings }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({ email: '', password: '', name: '', mobile: '' });
  const [adminLoading, setAdminLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Per-client package editor state — opened from the Details dialog.
  const [pkgEditOpen, setPkgEditOpen] = useState(false);
  const [pkgEditForm, setPkgEditForm] = useState(null);   // { package_id, features:{...}, entertainment:{...} }
  const [pkgEditSaving, setPkgEditSaving] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === clients.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(clients.map(c => c.id)));
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const { data } = await axios.post(`${API}/admin/clients/bulk-delete`,
        { restaurant_ids: Array.from(selectedIds) }, { withCredentials: true });
      toast.success(`Deleted ${data.deleted_restaurants} client${data.deleted_restaurants !== 1 ? 's' : ''}`);
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      fetchClients();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setBulkDeleting(false); }
  };

  const openDetail = async (rid) => {
    try {
      const { data } = await axios.get(`${API}/admin/clients/${rid}`, { withCredentials: true });
      setSelectedClient(data);
      setDetailOpen(true);
    } catch { toast.error('Failed to load'); }
  };

  const toggleStatus = async (rid, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    try {
      await axios.put(`${API}/admin/clients/${rid}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Client ${newStatus}`);
      fetchClients();
      if (selectedClient?.id === rid) setSelectedClient(prev => ({ ...prev, subscription_status: newStatus }));
    } catch { toast.error('Failed'); }
  };

  // ----- Per-client package editor helpers -----
  // Canonical list — mirrors backend CAPABILITY_KEYS. Order matches the
  // user's reference list (Tables, Food Menu, Food Ordering, …).
  const CAPABILITY_KEYS = [
    { key: 'tables',               label: 'Tables' },
    { key: 'food_menu',            label: 'Food Menu' },
    { key: 'food_ordering',        label: 'Food Ordering' },
    { key: 'call_reasons',         label: 'Call Reasons' },
    { key: 'live_calls',           label: 'Live Calls' },
    { key: 'chat',                 label: 'Chat' },
    { key: 'qr_codes',             label: 'QR Codes' },
    { key: 'paging',               label: 'Paging' },
    { key: 'inbox',                label: 'Inbox' },
    { key: 'access_control',       label: 'Access Control' },
    { key: 'settings',             label: 'Settings' },
    { key: 'entertainment',        label: 'Entertainment' },
    { key: 'customer_view_custom', label: 'Customization of Customer view' },
    { key: 'performance',          label: 'Performance' },
    { key: 'kpi_settings',         label: 'KPI settings' },
    { key: 'voice_calling',        label: 'Voice calling' },
    { key: 'report_analysis',      label: 'Report Analysis' },
    { key: 'translation',          label: 'Translation' },
  ];
  const ENT_KEYS = [
    { key: 'games',  label: 'Mini Games' },
    { key: 'quotes', label: 'Love Quotes' },
    { key: 'poems',  label: 'Poems' },
    { key: 'jokes',  label: 'Jokes' },
  ];
  const openPackageEditor = () => {
    if (!selectedClient) return;
    // Pre-fill from the client's current snapshot. ANY missing capability
    // defaults to TRUE so we honor the "by default all services enabled"
    // requirement — admin can flip individual ones off if they want.
    const snapshot = selectedClient.features || {};
    const features = Object.fromEntries(CAPABILITY_KEYS.map(c => [c.key, snapshot[c.key] === undefined ? true : !!snapshot[c.key]]));
    const ent = {
      games:  selectedClient.cust_games_enabled  !== false,
      quotes: selectedClient.cust_quotes_enabled !== false,
      poems:  selectedClient.cust_poems_enabled  !== false,
      jokes:  selectedClient.cust_jokes_enabled  !== false,
    };
    setPkgEditForm({
      package_id: selectedClient.package_id || (settings?.packages?.[0]?.id) || 'monthly',
      features,
      entertainment: ent,
    });
    setPkgEditOpen(true);
  };
  const resetAllOn = () => {
    setPkgEditForm(prev => ({
      ...prev,
      features: Object.fromEntries(CAPABILITY_KEYS.map(c => [c.key, true])),
      entertainment: Object.fromEntries(ENT_KEYS.map(c => [c.key, true])),
    }));
  };
  const savePackageEdit = async () => {
    if (!selectedClient || !pkgEditForm) return;
    setPkgEditSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/clients/${selectedClient.id}/package`,
        pkgEditForm,
        { withCredentials: true },
      );
      toast.success('Client package updated');
      setSelectedClient(prev => ({ ...prev, ...data }));
      setPkgEditOpen(false);
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setPkgEditSaving(false);
  };

  const createAdmin = async () => {
    if (!adminForm.email || !adminForm.password || !adminForm.name) { toast.error('Fill all required fields'); return; }
    setAdminLoading(true);
    try {
      await axios.post(`${API}/admin/create-admin`, adminForm, { withCredentials: true });
      toast.success(`Admin ${adminForm.email} created`);
      setAdminDialogOpen(false);
      setAdminForm({ email: '', password: '', name: '', mobile: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setAdminLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Clients</h2>
          <p className="text-sm text-stone-500 mt-1">{clients.length} registered restaurant{clients.length !== 1 ? 's' : ''}{selectedIds.size > 0 ? ` · ${selectedIds.size} selected` : ''}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {clients.length > 0 && (
            <Button data-testid="select-all-btn" variant="outline" size="sm" onClick={toggleSelectAll} className="gap-2 text-xs">
              {selectedIds.size === clients.length && clients.length > 0 ? 'Deselect all' : 'Select all'}
            </Button>
          )}
          {selectedIds.size > 0 && (
            <Button data-testid="bulk-delete-btn" size="sm" onClick={() => setBulkDeleteOpen(true)} className="gap-2 text-xs bg-red-600 hover:bg-red-700 text-white">
              <Trash2 size={14} /> Delete ({selectedIds.size})
            </Button>
          )}
          <Button data-testid="create-admin-btn" onClick={() => setAdminDialogOpen(true)} variant="outline" className="gap-2 text-sm border-stone-300">
            <Users size={16} /> Create Admin User
          </Button>
        </div>
      </div>

      {clients.length === 0 && (
        <Card className="border-stone-200 border-dashed">
          <CardContent className="py-12 text-center">
            <Store className="mx-auto text-stone-300 mb-3" size={40} />
            <p className="text-stone-500">No restaurants registered yet.</p>
          </CardContent>
        </Card>
      )}

      {clients.length > 0 && (
        <div className="border border-stone-200 rounded-xl bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="clients-table" className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200 text-stone-600">
                <tr>
                  <th className="px-3 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      data-testid="clients-table-select-all"
                      checked={selectedIds.size === clients.length && clients.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-stone-300 text-orange-600 focus:ring-orange-500 cursor-pointer"
                    />
                  </th>
                  <th className="px-3 py-3 text-left font-semibold">Restaurant</th>
                  <th className="px-3 py-3 text-left font-semibold">Owner</th>
                  <th className="px-3 py-3 text-left font-semibold">Email</th>
                  <th className="px-3 py-3 text-left font-semibold">Mobile</th>
                  <th className="px-3 py-3 text-left font-semibold">WhatsApp</th>
                  <th className="px-3 py-3 text-left font-semibold">Country</th>
                  <th className="px-3 py-3 text-left font-semibold">Joined</th>
                  <th className="px-3 py-3 text-left font-semibold">Status</th>
                  <th className="px-3 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {clients.map(c => {
                  const selected = selectedIds.has(c.id);
                  const owner = c.owner || {};
                  const country = countryFromPhone(owner.mobile || owner.whatsapp || '');
                  return (
                    <tr
                      key={c.id}
                      data-testid={`client-row-${c.id}`}
                      className={`${selected ? 'bg-orange-50/50' : 'hover:bg-stone-50'} transition-colors`}
                    >
                      <td className="px-3 py-3">
                        <input
                          data-testid={`client-select-${c.id}`}
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelect(c.id)}
                          className="w-4 h-4 rounded border-stone-300 text-orange-600 focus:ring-orange-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
                            <Store className="text-orange-600" size={14} />
                          </div>
                          <span className="font-semibold text-stone-900 truncate max-w-[160px]" title={c.name}>{c.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-stone-700 whitespace-nowrap">{owner.name || '—'}</td>
                      <td className="px-3 py-3 text-stone-700 whitespace-nowrap">
                        <span className="truncate inline-block max-w-[180px] align-bottom" title={owner.email || ''}>{owner.email || '—'}</span>
                      </td>
                      <td className="px-3 py-3 text-stone-700 whitespace-nowrap font-mono text-xs">{owner.mobile || '—'}</td>
                      <td className="px-3 py-3 text-stone-700 whitespace-nowrap font-mono text-xs">{owner.whatsapp || '—'}</td>
                      <td className="px-3 py-3 text-stone-700 whitespace-nowrap">
                        {country.code ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="text-base leading-none">{country.flag}</span>
                            <span className="text-xs">{country.name}</span>
                          </span>
                        ) : <span className="text-stone-400">—</span>}
                      </td>
                      <td className="px-3 py-3 text-stone-500 text-xs whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                      <td className="px-3 py-3">
                        <Badge className={c.subscription_status === 'active' ? 'bg-green-100 text-green-700 border-green-200' : c.subscription_status === 'suspended' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-stone-100 text-stone-700 border-stone-200'}>
                          {c.subscription_status || 'active'}
                        </Badge>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="outline" size="sm" onClick={() => openDetail(c.id)} className="gap-1 text-xs h-8" data-testid={`client-details-btn-${c.id}`}>
                            <Eye size={12} /> Details
                          </Button>
                          <Button variant="outline" size="sm"
                            onClick={() => toggleStatus(c.id, c.subscription_status || 'active')}
                            data-testid={`client-status-btn-${c.id}`}
                            className={`gap-1 text-xs h-8 ${c.subscription_status === 'active' ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}>
                            {c.subscription_status === 'active' ? <><EyeOff size={12} /> Suspend</> : <><Eye size={12} /> Activate</>}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation */}
      <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Delete {selectedIds.size} client{selectedIds.size !== 1 ? 's' : ''}?</DialogTitle>
            <p className="text-sm text-stone-500 pt-1">
              This permanently removes the selected restaurants, their host accounts, tables, menus, call history, chats and ratings. This cannot be undone.
            </p>
          </DialogHeader>
          <div className="max-h-40 overflow-y-auto bg-stone-50 border border-stone-200 rounded-lg p-3 my-2 space-y-1">
            {Array.from(selectedIds).map(id => {
              const c = clients.find(x => x.id === id);
              if (!c) return null;
              return <p key={id} className="text-xs text-stone-600">&middot; <span className="font-medium text-stone-800">{c.name}</span> <span className="text-stone-400">({c.owner?.email || '—'})</span></p>;
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>Cancel</Button>
            <Button data-testid="bulk-delete-confirm-btn" onClick={bulkDelete} disabled={bulkDeleting} className="bg-red-600 hover:bg-red-700 text-white">
              {bulkDeleting ? 'Deleting…' : `Yes, delete ${selectedIds.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>{selectedClient?.name}</DialogTitle>
            <p className="text-sm text-stone-500">{selectedClient?.owner?.email}</p>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Tables', value: selectedClient.stats?.tables, icon: LayoutGrid },
                  { label: 'Total Calls', value: selectedClient.stats?.calls_total, icon: Bell },
                  { label: 'Active Calls', value: selectedClient.stats?.calls_active, icon: AlertTriangle },
                  { label: 'Menu Items', value: selectedClient.stats?.menu_items, icon: UtensilsCrossed },
                ].map((stat, i) => (
                  <div key={i} className="p-3 rounded-lg bg-stone-50 text-center">
                    <stat.icon size={16} className="mx-auto text-stone-400 mb-1" />
                    <p className="text-lg font-bold text-stone-900">{stat.value || 0}</p>
                    <p className="text-[10px] text-stone-500">{stat.label}</p>
                  </div>
                ))}
              </div>
              <Separator />
              <div className="text-sm space-y-1">
                <p><span className="text-stone-500">Owner Name:</span> <span className="font-medium">{selectedClient.owner?.name || '—'}</span></p>
                <p><span className="text-stone-500">Email:</span> <span className="font-medium">{selectedClient.owner?.email || '—'}</span></p>
                <p><span className="text-stone-500">Mobile:</span> <span className="font-mono">{selectedClient.owner?.mobile || '—'}</span></p>
                <p><span className="text-stone-500">WhatsApp:</span> <span className="font-mono">{selectedClient.owner?.whatsapp || '—'}</span></p>
                {(() => {
                  const cty = countryFromPhone(selectedClient.owner?.mobile || selectedClient.owner?.whatsapp || '');
                  return (
                    <p><span className="text-stone-500">Country:</span>{' '}
                      <span className="font-medium">{cty.code ? `${cty.flag} ${cty.name}` : '—'}</span>
                    </p>
                  );
                })()}
                <p><span className="text-stone-500">Theme:</span> <span className="font-medium">{selectedClient.theme}</span></p>
                <p><span className="text-stone-500">Status:</span> <Badge className={selectedClient.subscription_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{selectedClient.subscription_status}</Badge></p>
                <p><span className="text-stone-500">Package:</span>{' '}
                  <span className="font-medium">
                    {(settings?.packages || []).find(p => p.id === selectedClient.package_id)?.name
                      || selectedClient.package_id
                      || '—'}
                  </span>
                </p>
                <p><span className="text-stone-500">Description:</span> {selectedClient.description || '—'}</p>
                <p><span className="text-stone-500">Created:</span> {new Date(selectedClient.created_at).toLocaleString()}</p>
              </div>
              {/* Per-client package & features editor entry */}
              <Separator />
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-stone-700">Package &amp; Features</p>
                  <p className="text-xs text-stone-500">Change this client's plan or toggle individual capabilities. Defaults to all enabled.</p>
                </div>
                <Button
                  data-testid="edit-client-package-btn"
                  size="sm"
                  variant="outline"
                  onClick={openPackageEditor}
                  className="gap-1.5 border-orange-200 text-orange-700 hover:bg-orange-50"
                >
                  <Edit2 size={12} /> Edit Package
                </Button>
              </div>
              {selectedClient.tables?.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm font-semibold text-stone-700 mb-2">Tables</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedClient.tables.map(t => (
                        <Badge key={t.id} variant="outline" className="text-xs">{t.label} ({t.seats} seats)</Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Client Package Dialog */}
      <Dialog open={pkgEditOpen} onOpenChange={setPkgEditOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Edit Package</DialogTitle>
            <p className="text-sm text-stone-500">{selectedClient?.name}</p>
          </DialogHeader>
          {pkgEditForm && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Package Tier</Label>
                <select
                  data-testid="edit-client-package-select"
                  value={pkgEditForm.package_id}
                  onChange={e => setPkgEditForm(p => ({ ...p, package_id: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-stone-200 bg-white text-sm"
                >
                  {(settings?.packages || []).map(p => (
                    <option key={p.id} value={p.id}>{p.name} {p.price ? `(${p.price}${p.period || ''})` : ''}</option>
                  ))}
                </select>
                <p className="text-xs text-stone-500">Tier label is informational — the toggles below control what the host actually sees.</p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Host Capabilities</Label>
                  <button
                    type="button"
                    onClick={resetAllOn}
                    data-testid="edit-client-package-reset-all-btn"
                    className="text-[11px] font-semibold text-orange-600 hover:text-orange-700"
                  >
                    Reset to all enabled
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {CAPABILITY_KEYS.map(c => (
                    <label
                      key={c.key}
                      data-testid={`edit-client-cap-${c.key}`}
                      className="flex items-center gap-2 px-3 py-2 rounded-md border border-stone-200 cursor-pointer hover:bg-stone-50"
                    >
                      <input
                        type="checkbox"
                        checked={!!pkgEditForm.features[c.key]}
                        onChange={e => setPkgEditForm(p => ({ ...p, features: { ...p.features, [c.key]: e.target.checked } }))}
                        className="w-4 h-4 rounded border-stone-300 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm text-stone-700">{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Customer Entertainment (host-level opt-ins)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {ENT_KEYS.map(c => (
                    <label
                      key={c.key}
                      data-testid={`edit-client-ent-${c.key}`}
                      className="flex items-center gap-2 px-3 py-2 rounded-md border border-stone-200 cursor-pointer hover:bg-stone-50"
                    >
                      <input
                        type="checkbox"
                        checked={!!pkgEditForm.entertainment[c.key]}
                        onChange={e => setPkgEditForm(p => ({ ...p, entertainment: { ...p.entertainment, [c.key]: e.target.checked } }))}
                        className="w-4 h-4 rounded border-stone-300 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm text-stone-700">{c.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-stone-500">
                  These are the same toggles the host sees in their Settings — flip them on here so the client immediately has every entertainment block available.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPkgEditOpen(false)} disabled={pkgEditSaving}>Cancel</Button>
            <Button
              data-testid="edit-client-package-save-btn"
              onClick={savePackageEdit}
              disabled={pkgEditSaving}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {pkgEditSaving ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Create Admin Dialog */}
      <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Create Admin User</DialogTitle>
            <p className="text-sm text-stone-500">This user will have full site management access</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input value={adminForm.name} onChange={e => setAdminForm(p => ({ ...p, name: e.target.value }))} placeholder="Admin Name" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={adminForm.email} onChange={e => setAdminForm(p => ({ ...p, email: e.target.value }))} placeholder="admin@example.com" />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input type="password" value={adminForm.password} onChange={e => setAdminForm(p => ({ ...p, password: e.target.value }))} placeholder="Min 6 characters" />
            </div>
            <div className="space-y-2">
              <Label>Mobile (optional)</Label>
              <Input type="tel" value={adminForm.mobile} onChange={e => setAdminForm(p => ({ ...p, mobile: e.target.value }))} placeholder="+960..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdminDialogOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-create-admin-btn" onClick={createAdmin} disabled={adminLoading} className="bg-stone-900 hover:bg-stone-800 text-white">
              {adminLoading ? 'Creating...' : 'Create Admin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ==================== PACKAGES & PAYMENT PANEL ====================
function PackagesPanel({ settings, setSettings }) {
  const [packages, setPackages] = useState([]);
  const [paymentInstructions, setPaymentInstructions] = useState('');
  const [paymentWhatsapp, setPaymentWhatsapp] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingPkg, setEditingPkg] = useState(null);
  const [pkgDialogOpen, setPkgDialogOpen] = useState(false);
  const [pkgForm, setPkgForm] = useState({ id: '', name: '', price: '', period: '', features: [], popular: false });
  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    if (settings) {
      setPackages(settings.packages || []);
      setPaymentInstructions(settings.payment_instructions || '');
      setPaymentWhatsapp(settings.payment_whatsapp || '');
    }
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/site-settings`, {
        packages, payment_instructions: paymentInstructions, payment_whatsapp: paymentWhatsapp
      }, { withCredentials: true });
      setSettings(data);
      toast.success('Packages & payment settings saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const openNewPkg = () => {
    setEditingPkg(null);
    setPkgForm({
      id: `pkg_${Date.now()}`, name: '', price: '', period: '/month', features: [], popular: false,
      entertainment: { games: false, quotes: false, poems: false, jokes: false },
      // Default new packages with EVERY capability enabled, matching the
      // user's spec "all these features should be ticked for host/clients".
      capabilities: {
        tables: true, food_menu: true, food_ordering: true, call_reasons: true,
        live_calls: true, chat: true, qr_codes: true, paging: true, inbox: true,
        access_control: true, settings: true, entertainment: true,
        customer_view_custom: true, performance: true, kpi_settings: true,
        voice_calling: true, report_analysis: true, translation: true,
      },
    });
    setFeatureInput('');
    setPkgDialogOpen(true);
  };

  const openEditPkg = (pkg, idx) => {
    setEditingPkg(idx);
    setPkgForm({
      ...pkg,
      entertainment: { games: false, quotes: false, poems: false, jokes: false, ...(pkg.entertainment || {}) },
      capabilities: {
        tables: true, food_menu: true, food_ordering: true, call_reasons: true,
        live_calls: true, chat: true, qr_codes: true, paging: true, inbox: true,
        access_control: true, settings: true, entertainment: true,
        customer_view_custom: true, performance: true, kpi_settings: true,
        voice_calling: true, report_analysis: true, translation: true,
        ...(pkg.capabilities || {}),
      },
    });
    setFeatureInput('');
    setPkgDialogOpen(true);
  };

  const savePkg = () => {
    if (!pkgForm.name || !pkgForm.price) { toast.error('Name and price required'); return; }
    if (editingPkg !== null) {
      setPackages(prev => prev.map((p, i) => i === editingPkg ? pkgForm : p));
    } else {
      setPackages(prev => [...prev, pkgForm]);
    }
    setPkgDialogOpen(false);
  };

  const deletePkg = (idx) => { setPackages(prev => prev.filter((_, i) => i !== idx)); };

  const addFeature = () => {
    if (!featureInput.trim()) return;
    setPkgForm(prev => ({ ...prev, features: [...prev.features, featureInput.trim()] }));
    setFeatureInput('');
  };

  const removeFeature = (idx) => {
    setPkgForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Packages & Payment</h2>
          <p className="text-sm text-stone-500 mt-1">Manage pricing packages and payment instructions</p>
        </div>
        <Button data-testid="save-packages-btn" onClick={save} disabled={saving} className="bg-stone-900 hover:bg-stone-800 text-white">
          {saving ? 'Saving...' : 'Save All'}
        </Button>
      </div>

      {/* Package List */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Pricing Packages</CardTitle>
          <Button data-testid="add-package-btn" variant="outline" size="sm" onClick={openNewPkg} className="gap-1 text-xs">+ Add Package</Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {packages.length === 0 && <p className="text-sm text-stone-400 text-center py-4">No packages yet. Add your first pricing package.</p>}
          {packages.map((pkg, i) => (
            <div key={pkg.id || i} className="flex items-center gap-4 p-3 rounded-xl border border-stone-100 bg-stone-50/50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-stone-900">{pkg.name}</p>
                  {pkg.popular && <Badge className="bg-orange-100 text-orange-700 text-[10px]">Popular</Badge>}
                </div>
                <p className="text-lg font-bold text-stone-900">${pkg.price} <span className="text-sm font-normal text-stone-500">{pkg.period}</span></p>
                <p className="text-xs text-stone-500">{(pkg.features || []).join(' · ')}</p>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEditPkg(pkg, i)} className="h-8 w-8"><Edit2 size={14} /></Button>
                <Button variant="ghost" size="icon" onClick={() => deletePkg(i)} className="h-8 w-8 text-red-500"><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Payment Instructions */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Payment Instructions</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Instructions Text</Label>
            <p className="text-xs text-stone-400">Shown to customers on the landing page and to hosts when they need to pay.</p>
            <Textarea data-testid="payment-instructions-input" value={paymentInstructions} onChange={e => setPaymentInstructions(e.target.value)} rows={4} placeholder="To complete your payment, please contact us..." />
          </div>
          <div className="space-y-2">
            <Label>WhatsApp Payment Link</Label>
            <p className="text-xs text-stone-400">Customers and hosts will see a "Chat on WhatsApp" button linking here.</p>
            <Input data-testid="payment-whatsapp-input" value={paymentWhatsapp} onChange={e => setPaymentWhatsapp(e.target.value)} placeholder="https://wa.me/1234567890?text=Payment inquiry" />
          </div>
        </CardContent>
      </Card>

      {/* Package Edit Dialog */}
      <Dialog open={pkgDialogOpen} onOpenChange={setPkgDialogOpen}>
        {/* Wide dialog so all 17 capability toggles fit in a 3-column grid
            without forcing the user to scroll a tiny column. max-h with
            internal scroll prevents the footer from going off-screen on
            smaller laptops. */}
        <DialogContent className="w-[95vw] sm:max-w-3xl lg:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>{editingPkg !== null ? 'Edit Package' : 'Add Package'}</DialogTitle>
            <p className="text-sm text-stone-500">Define pricing and features for this subscription tier.</p>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Package Name</Label>
                <Input className="h-11 text-base" value={pkgForm.name} onChange={e => setPkgForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Monthly" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Price ($)</Label>
                <Input className="h-11 text-base" value={pkgForm.price} onChange={e => setPkgForm(p => ({ ...p, price: e.target.value }))} placeholder="29" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Period</Label>
                <select value={pkgForm.period} onChange={e => setPkgForm(p => ({ ...p, period: e.target.value }))} className="w-full h-11 px-3 rounded-md border border-stone-200 bg-white text-base">
                  <option value="one-time">One-time</option>
                  <option value="/month">/month</option>
                  <option value="/6 months">/6 months</option>
                  <option value="/year">/year</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Popular?</Label>
                <div className="flex items-center gap-2 h-11">
                  <input type="checkbox" checked={pkgForm.popular || false} onChange={e => setPkgForm(p => ({ ...p, popular: e.target.checked }))} className="w-5 h-5 accent-orange-600" />
                  <span className="text-base text-stone-600">Mark as popular</span>
                </div>
              </div>
            </div>
            {/* Customer-page entertainment unlocks. Each checkbox is a toggle the
                host's Settings panel uses to gate which buttons are offered. */}
            <div className="space-y-2">
              <Label className="text-base font-bold">Customer Entertainment unlocks</Label>
              <p className="text-sm text-stone-500 -mt-1">Select which entertainment buttons are available to hosts on this package.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { key: 'games',  label: 'Mini Games' },
                  { key: 'quotes', label: 'Love Quotes' },
                  { key: 'poems',  label: 'Poems' },
                  { key: 'jokes',  label: 'Jokes' },
                ].map(opt => {
                  const checked = !!(pkgForm.entertainment || {})[opt.key];
                  return (
                    <label key={opt.key} className="flex items-center gap-2 p-3 rounded-md border border-stone-200 cursor-pointer hover:bg-stone-50">
                      <input
                        type="checkbox"
                        data-testid={`pkg-ent-${opt.key}`}
                        checked={checked}
                        onChange={e => setPkgForm(p => ({ ...p, entertainment: { ...(p.entertainment || {}), [opt.key]: e.target.checked } }))}
                        className="w-5 h-5 accent-orange-600"
                      />
                      <span className="text-sm font-medium text-stone-700">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-base font-bold">Host capabilities</Label>
              <p className="text-sm text-stone-500 -mt-1">What hosts can use on this package. Applies only to <span className="font-semibold">new sign-ups</span> — existing clients keep their current capabilities unless edited individually.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { key: 'tables',               label: 'Tables' },
                  { key: 'food_menu',            label: 'Food Menu' },
                  { key: 'food_ordering',        label: 'Food Ordering' },
                  { key: 'call_reasons',         label: 'Call Reasons' },
                  { key: 'live_calls',           label: 'Live Calls' },
                  { key: 'chat',                 label: 'Chat' },
                  { key: 'qr_codes',             label: 'QR Codes' },
                  { key: 'paging',               label: 'Paging' },
                  { key: 'inbox',                label: 'Inbox' },
                  { key: 'access_control',       label: 'Access Control' },
                  { key: 'settings',             label: 'Settings' },
                  { key: 'entertainment',        label: 'Entertainment' },
                  { key: 'customer_view_custom', label: 'Customization of Customer view' },
                  { key: 'performance',          label: 'Performance' },
                  { key: 'kpi_settings',         label: 'KPI settings' },
                  { key: 'voice_calling',        label: 'Voice calling' },
                  { key: 'report_analysis',      label: 'Report Analysis' },
                  { key: 'translation',          label: 'Translation' },
                ].map(opt => {
                  const checked = !!(pkgForm.capabilities || {})[opt.key];
                  return (
                    <label key={opt.key} className="flex items-center gap-2 p-3 rounded-md border border-stone-200 cursor-pointer hover:bg-stone-50">
                      <input
                        type="checkbox"
                        data-testid={`pkg-cap-${opt.key}`}
                        checked={checked}
                        onChange={e => setPkgForm(p => ({ ...p, capabilities: { ...(p.capabilities || {}), [opt.key]: e.target.checked } }))}
                        className="w-5 h-5 accent-orange-600"
                      />
                      <span className="text-sm font-medium text-stone-700">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-base font-bold">Features</Label>
              <div className="flex gap-2">
                <Input className="h-11 text-base" value={featureInput} onChange={e => setFeatureInput(e.target.value)} placeholder="e.g. Unlimited tables" onKeyDown={e => e.key === 'Enter' && addFeature()} />
                <Button variant="outline" onClick={addFeature} className="h-11 px-4">Add</Button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {(pkgForm.features || []).map((f, i) => (
                  <Badge key={i} variant="outline" className="gap-1 pr-1 text-sm py-1">
                    {f}
                    <button onClick={() => removeFeature(i)} className="ml-1 text-red-400 hover:text-red-600">&times;</button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPkgDialogOpen(false)}>Cancel</Button>
            <Button onClick={savePkg} className="bg-stone-900 hover:bg-stone-800 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ==================== SITE SETTINGS PANEL ====================
function SiteSettingsPanel({ settings, setSettings }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);

  useEffect(() => {
    if (settings) setForm({ ...settings });
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const { id, _id, ...rest } = form;
      const { data } = await axios.put(`${API}/admin/site-settings`, rest, { withCredentials: true });
      setSettings(data);
      toast.success('Site settings saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const uploadImage = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(field);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, [field]: data.path }));
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(null);
  };

  // Showcase box 2 can be image OR video — we also record the type to render correctly on Landing
  const uploadMedia = async (e, field, typeField) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(field);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const kind = (file.type || '').startsWith('video/') ? 'video' : 'image';
      setForm(prev => ({ ...prev, [field]: data.path, [typeField]: kind }));
      toast.success(`${kind === 'video' ? 'Video' : 'Image'} uploaded`);
    } catch { toast.error('Upload failed'); }
    setUploading(null);
  };

  const u = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const addFooterLink = () => {
    setForm(prev => ({ ...prev, footer_links: [...(prev.footer_links || []), { label: '', url: '' }] }));
  };

  const updateFooterLink = (idx, field, value) => {
    setForm(prev => {
      const links = [...(prev.footer_links || [])];
      links[idx] = { ...links[idx], [field]: value };
      return { ...prev, footer_links: links };
    });
  };

  const removeFooterLink = (idx) => {
    setForm(prev => ({ ...prev, footer_links: (prev.footer_links || []).filter((_, i) => i !== idx) }));
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Site Settings</h2>
          <p className="text-sm text-stone-500 mt-1">Customize landing page, footer, and customer-facing defaults</p>
        </div>
        <Button data-testid="save-site-settings-btn" onClick={save} disabled={saving} className="bg-stone-900 hover:bg-stone-800 text-white">
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>

      {/* Site Branding */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Site Branding</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Site Name</Label>
            <Input data-testid="site-name-input" value={form.site_name || ''} onChange={u('site_name')} placeholder="CallButton" />
            <p className="text-xs text-stone-400">Shown in the header and footer of the landing page</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-xl border-2 border-dashed border-stone-300 flex items-center justify-center bg-stone-50 overflow-hidden">
              {form.site_logo_path ? <ImagePreview path={form.site_logo_path} /> : <Upload className="text-stone-300" size={20} />}
            </div>
            <div>
              <Label className="text-xs text-stone-500">Header Logo</Label>
              <label className="cursor-pointer block mt-1">
                <Input type="file" accept="image/*" onChange={e => uploadImage(e, 'site_logo_path')} className="hidden" />
                <span className="text-xs text-orange-600 hover:text-orange-700 font-medium cursor-pointer">
                  {uploading === 'site_logo_path' ? 'Uploading...' : form.site_logo_path ? 'Change' : 'Upload'}
                </span>
              </label>
            </div>
            <div className="ml-auto space-y-1">
              <Label className="text-xs text-stone-500">Size: {form.header_logo_size || 36}px</Label>
              <input type="range" min={20} max={120} value={form.header_logo_size || 36} onChange={e => setForm(p => ({ ...p, header_logo_size: parseInt(e.target.value) }))} className="w-24 accent-orange-600" />
            </div>
          </div>

          {/* Loading splash logo — shown during initial page load with a zoom animation */}
          <div className="flex items-center gap-4 pt-2 border-t border-stone-100">
            <div className="w-16 h-16 rounded-xl border-2 border-dashed border-stone-300 flex items-center justify-center bg-stone-50 overflow-hidden">
              {form.loading_logo_path ? <ImagePreview path={form.loading_logo_path} /> : <Upload className="text-stone-300" size={20} />}
            </div>
            <div className="flex-1">
              <Label className="text-xs text-stone-500">Loading Splash Logo</Label>
              <p className="text-[11px] text-stone-400 mt-0.5">Shown while the site is loading. Animates with a smooth zoom in/out.</p>
              <div className="flex items-center gap-2 mt-1">
                <label className="cursor-pointer">
                  <Input type="file" accept="image/*" onChange={e => uploadImage(e, 'loading_logo_path')} className="hidden" data-testid="loading-logo-input" />
                  <span className="text-xs text-orange-600 hover:text-orange-700 font-medium cursor-pointer">
                    {uploading === 'loading_logo_path' ? 'Uploading...' : form.loading_logo_path ? 'Change' : 'Upload'}
                  </span>
                </label>
                {form.loading_logo_path && (
                  <button
                    type="button"
                    data-testid="loading-logo-clear"
                    onClick={() => setForm(p => ({ ...p, loading_logo_path: '' }))}
                    className="text-xs text-red-600 hover:text-red-700"
                  >Remove</button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hero Section */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Hero Section</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Hero Title</Label>
            <Input data-testid="hero-title-input" value={form.hero_title || ''} onChange={u('hero_title')} placeholder="Your Tables, Connected." />
          </div>
          <div className="space-y-2">
            <Label>Hero Subtitle</Label>
            <Textarea value={form.hero_subtitle || ''} onChange={u('hero_subtitle')} rows={2} placeholder="Give your customers..." />
          </div>
          <div className="space-y-2">
            <Label>CTA Button Text</Label>
            <Input value={form.hero_cta_text || ''} onChange={u('hero_cta_text')} placeholder="Start Free Trial" />
          </div>
          <div className="space-y-2">
            <Label>Hero Image</Label>
            <div className="flex items-center gap-3">
              <label className="cursor-pointer">
                <Input type="file" accept="image/*" onChange={e => uploadImage(e, 'hero_image_path')} className="hidden" />
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700 text-sm font-medium cursor-pointer">
                  <Upload size={14} /> {uploading === 'hero_image_path' ? 'Uploading...' : form.hero_image_path ? 'Change Image' : 'Upload Image'}
                </span>
              </label>
              {form.hero_image_path && <span className="text-xs text-green-600">Image set</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Showcase — 2 boxes shown above the Features section on the landing page */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Showcase (Landing page)</CardTitle>
          <p className="text-xs text-stone-500 mt-1">Two boxes appear just above &quot;Everything your restaurant needs&quot;. Box&nbsp;1 is a 1&times;1 square (image). Box&nbsp;2 is a 2&times;1 wide panel (image or video, auto-plays muted).</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Box 1 — image only */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-stone-500 mb-1.5 block">Box 1 — Image (1×1)</Label>
              <label className="block cursor-pointer">
                <Input type="file" accept="image/*" onChange={e => uploadImage(e, 'showcase_image_path')} className="hidden" data-testid="showcase-box1-input" />
                <div className="aspect-square rounded-xl border-2 border-dashed border-stone-300 hover:border-orange-400 transition-colors bg-stone-50 overflow-hidden flex items-center justify-center">
                  {form.showcase_image_path
                    ? <ImagePreview path={form.showcase_image_path} />
                    : <div className="text-stone-400 flex flex-col items-center gap-1"><Upload size={18} /><span className="text-xs">Click to upload</span></div>}
                </div>
              </label>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-stone-500">{uploading === 'showcase_image_path' ? 'Uploading…' : form.showcase_image_path ? 'Image set' : 'No image'}</span>
                {form.showcase_image_path && (
                  <button
                    type="button"
                    data-testid="showcase-box1-clear"
                    onClick={() => setForm(p => ({ ...p, showcase_image_path: '' }))}
                    className="text-xs text-red-600 hover:text-red-700"
                  >Remove</button>
                )}
              </div>
            </div>

            {/* Box 2 — image or video (2x1) */}
            <div className="md:col-span-2">
              <Label className="text-xs text-stone-500 mb-1.5 block">Box 2 — Image or Video (2×1 wide)</Label>
              <label className="block cursor-pointer">
                <Input
                  type="file"
                  accept="image/*,video/*"
                  onChange={e => uploadMedia(e, 'showcase_media_path', 'showcase_media_type')}
                  className="hidden"
                  data-testid="showcase-box2-input"
                />
                <div className="aspect-[2/1] rounded-xl border-2 border-dashed border-stone-300 hover:border-orange-400 transition-colors bg-stone-50 overflow-hidden flex items-center justify-center">
                  {form.showcase_media_path ? (
                    form.showcase_media_type === 'video'
                      ? <MediaVideoPreview path={form.showcase_media_path} />
                      : <ImagePreview path={form.showcase_media_path} />
                  ) : (
                    <div className="text-stone-400 flex flex-col items-center gap-1"><Upload size={18} /><span className="text-xs">Click to upload image or video</span></div>
                  )}
                </div>
              </label>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-stone-500">
                  {uploading === 'showcase_media_path'
                    ? 'Uploading…'
                    : form.showcase_media_path
                      ? `${form.showcase_media_type === 'video' ? 'Video' : 'Image'} set`
                      : 'No media'}
                </span>
                {form.showcase_media_path && (
                  <button
                    type="button"
                    data-testid="showcase-box2-clear"
                    onClick={() => setForm(p => ({ ...p, showcase_media_path: '', showcase_media_type: 'image' }))}
                    className="text-xs text-red-600 hover:text-red-700"
                  >Remove</button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Feature Boxes</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3, 4].map(n => (
            <div key={n} className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-stone-50">
              <div className="space-y-1">
                <Label className="text-xs">Feature {n} Title</Label>
                <Input value={form[`feature_${n}_title`] || ''} onChange={u(`feature_${n}_title`)} className="h-9 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Feature {n} Description</Label>
                <Input value={form[`feature_${n}_desc`] || ''} onChange={u(`feature_${n}_desc`)} className="h-9 text-sm" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Footer */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Footer</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Footer Text</Label>
            <Input value={form.footer_text || ''} onChange={u('footer_text')} placeholder="Restaurant service, simplified." />
          </div>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl border-2 border-dashed border-stone-300 flex items-center justify-center bg-stone-50 overflow-hidden">
              {form.footer_logo_path ? <ImagePreview path={form.footer_logo_path} /> : <Upload className="text-stone-300" size={16} />}
            </div>
            <div>
              <Label className="text-xs text-stone-500">Footer Logo</Label>
              <p className="text-[10px] text-stone-400 mb-1">Separate from header logo. Falls back to header logo if not set.</p>
              <label className="cursor-pointer">
                <Input type="file" accept="image/*" onChange={e => uploadImage(e, 'footer_logo_path')} className="hidden" />
                <span className="text-xs text-orange-600 hover:text-orange-700 font-medium cursor-pointer">
                  {uploading === 'footer_logo_path' ? 'Uploading...' : form.footer_logo_path ? 'Change' : 'Upload'}
                </span>
              </label>
            </div>
            <div className="ml-auto space-y-1">
              <Label className="text-xs text-stone-500">Size: {form.footer_logo_size || 28}px</Label>
              <input type="range" min={16} max={120} value={form.footer_logo_size || 28} onChange={e => setForm(p => ({ ...p, footer_logo_size: parseInt(e.target.value) }))} className="w-24 accent-orange-600" />
            </div>
          </div>
          <Separator />
          <div className="space-y-2">
            <Label>Footer Links</Label>
            {(form.footer_links || []).map((link, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input value={link.label} onChange={e => updateFooterLink(i, 'label', e.target.value)} placeholder="Label" className="h-9 text-sm" />
                <Input value={link.url} onChange={e => updateFooterLink(i, 'url', e.target.value)} placeholder="URL" className="h-9 text-sm" />
                <Button variant="ghost" size="icon" onClick={() => removeFooterLink(i)} className="h-8 w-8 text-red-500 flex-shrink-0">&times;</Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addFooterLink} className="text-xs gap-1">+ Add Link</Button>
          </div>
          <Separator />
          <div className="space-y-3">
            <Label>Social Media Links</Label>
            <p className="text-xs text-stone-400">Leave empty to hide. Icons appear in the footer.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-stone-500"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.98a8.2 8.2 0 004.76 1.52V7.05a4.84 4.84 0 01-1-.36z"/></svg>
                  TikTok
                </Label>
                <Input data-testid="social-tiktok" value={(form.social_links || {}).tiktok || ''} onChange={e => setForm(p => ({ ...p, social_links: { ...(p.social_links || {}), tiktok: e.target.value } }))} placeholder="https://tiktok.com/@..." className="h-9 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-stone-500"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                  Instagram
                </Label>
                <Input data-testid="social-instagram" value={(form.social_links || {}).instagram || ''} onChange={e => setForm(p => ({ ...p, social_links: { ...(p.social_links || {}), instagram: e.target.value } }))} placeholder="https://instagram.com/..." className="h-9 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-stone-500"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  Twitter / X
                </Label>
                <Input data-testid="social-twitter" value={(form.social_links || {}).twitter || ''} onChange={e => setForm(p => ({ ...p, social_links: { ...(p.social_links || {}), twitter: e.target.value } }))} placeholder="https://x.com/..." className="h-9 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-stone-500"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </Label>
                <Input data-testid="social-whatsapp" value={(form.social_links || {}).whatsapp || ''} onChange={e => setForm(p => ({ ...p, social_links: { ...(p.social_links || {}), whatsapp: e.target.value } }))} placeholder="https://wa.me/..." className="h-9 text-sm" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customer-facing defaults */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Customer Page Defaults</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Call Button Text</Label>
              <Input value={form.customer_call_button_text || ''} onChange={u('customer_call_button_text')} placeholder="Call Server" />
            </div>
            <div className="space-y-2">
              <Label>Menu Button Text</Label>
              <Input value={form.customer_menu_button_text || ''} onChange={u('customer_menu_button_text')} placeholder="View Menu" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Chat Label</Label>
              <Input value={form.customer_chat_label || ''} onChange={u('customer_chat_label')} placeholder="Chat with host" />
            </div>
            <div className="space-y-2">
              <Label>Welcome Text</Label>
              <Input value={form.customer_welcome_text || ''} onChange={u('customer_welcome_text')} placeholder="Optional welcome message" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trial Period */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Trial Period</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Trial Duration (days)</Label>
            <p className="text-xs text-stone-400">New registrations get this many days free. Set to 0 to disable trials. Max 365 days.</p>
            <div className="flex items-center gap-3">
              <Input type="number" min={0} max={365} value={form.trial_days ?? 7} onChange={e => setForm(p => ({ ...p, trial_days: parseInt(e.target.value) || 0 }))} className="w-24" />
              <span className="text-sm text-stone-500">days</span>
              {form.trial_days >= 90 && <span className="text-xs text-stone-400">≈ {Math.round(form.trial_days / 30)} month{Math.round(form.trial_days / 30) !== 1 ? 's' : ''}</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table Settings */}
      <Card className="border-stone-200">
        <CardHeader className="pb-3"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Table Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-stone-400">Control the minimum and maximum table size hosts can set in their layout editor.</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Minimum Table Size (px)</Label>
              <div className="flex items-center gap-2">
                <Input type="number" min={20} max={100} value={form.table_min_size ?? 30} onChange={e => setForm(p => ({ ...p, table_min_size: parseInt(e.target.value) || 30 }))} className="w-20" />
                <span className="text-xs text-stone-400">px</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Maximum Table Size (px)</Label>
              <div className="flex items-center gap-2">
                <Input type="number" min={100} max={400} value={form.table_max_size ?? 200} onChange={e => setForm(p => ({ ...p, table_max_size: parseInt(e.target.value) || 200 }))} className="w-20" />
                <span className="text-xs text-stone-400">px</span>
              </div>
            </div>
          </div>
          <div className="flex gap-3 items-center p-3 bg-stone-50 rounded-lg">
            <div className="border-2 border-stone-300 rounded-lg flex items-center justify-center" style={{ width: `${Math.min(form.table_min_size || 30, 60)}px`, height: `${Math.min(form.table_min_size || 30, 60)}px` }}>
              <span className="text-[8px] text-stone-400">Min</span>
            </div>
            <span className="text-stone-300">→</span>
            <div className="border-2 border-stone-300 rounded-lg flex items-center justify-center" style={{ width: `${Math.min(form.table_max_size || 200, 80)}px`, height: `${Math.min(form.table_max_size || 200, 80)}px` }}>
              <span className="text-[9px] text-stone-400">Max</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Management */}
      <UserManagementSection />
    </div>
  );
}


// ==================== USER MANAGEMENT SECTION ====================
function UserManagementSection() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '', mobile: '' });
  const [creating, setCreating] = useState(false);

  const fetchAdmins = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setAdmins(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const toggleActive = async (email) => {
    try {
      const { data } = await axios.put(`${API}/admin/users/${email}/toggle-active`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchAdmins();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`Delete admin ${email}?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${email}`, { withCredentials: true });
      toast.success('User deleted');
      fetchAdmins();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const createAdmin = async () => {
    if (!newAdmin.name || !newAdmin.email || !newAdmin.password) { toast.error('Fill required fields'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/create-admin`, newAdmin, { withCredentials: true });
      toast.success('Admin created');
      setCreateOpen(false);
      setNewAdmin({ name: '', email: '', password: '', mobile: '' });
      fetchAdmins();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setCreating(false);
  };

  return (
    <Card className="border-stone-200">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>User Management</CardTitle>
        <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)} className="gap-1 text-xs">
          <Users size={14} /> Add Admin
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-stone-400">Manage admin users who can access site management.</p>
        {loading ? (
          <div className="text-center py-4"><div className="w-6 h-6 border-2 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : admins.length === 0 ? (
          <p className="text-sm text-stone-400 text-center py-4">No admin users found.</p>
        ) : (
          <div className="space-y-2">
            {admins.map(a => (
              <div key={a.email} className="flex items-center gap-3 p-3 rounded-xl border border-stone-100 bg-stone-50/50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm text-stone-900">{a.name}</p>
                    <Badge variant="outline" className="text-[10px]">{a.role}</Badge>
                    {a.active === false && <Badge className="bg-red-100 text-red-700 text-[10px]">Deactivated</Badge>}
                  </div>
                  <p className="text-xs text-stone-500">{a.email}{a.mobile ? ` · ${a.mobile}` : ''}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => toggleActive(a.email)}
                    className={`text-xs h-7 ${a.active === false ? 'text-green-600' : 'text-amber-600'}`}>
                    {a.active === false ? 'Activate' : 'Deactivate'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteUser(a.email)} className="text-xs text-red-500 h-7">
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Admin Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Create Admin User</DialogTitle>
              <p className="text-sm text-stone-500">This user gets full site management access</p>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1.5"><Label>Name *</Label><Input value={newAdmin.name} onChange={e => setNewAdmin(p => ({ ...p, name: e.target.value }))} placeholder="Admin Name" /></div>
              <div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={newAdmin.email} onChange={e => setNewAdmin(p => ({ ...p, email: e.target.value }))} placeholder="admin@example.com" /></div>
              <div className="space-y-1.5"><Label>Password *</Label><Input type="password" value={newAdmin.password} onChange={e => setNewAdmin(p => ({ ...p, password: e.target.value }))} placeholder="Min 6 chars" /></div>
              <div className="space-y-1.5"><Label>Mobile</Label><Input type="tel" value={newAdmin.mobile} onChange={e => setNewAdmin(p => ({ ...p, mobile: e.target.value }))} placeholder="+960..." /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={createAdmin} disabled={creating} className="bg-stone-900 hover:bg-stone-800 text-white">
                {creating ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}


function ImagePreview({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    let revoke;
    axios.get(`${API}/files/${path}`, { responseType: 'blob', withCredentials: true })
      .then(r => { const url = URL.createObjectURL(r.data); revoke = url; setSrc(url); })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);
  if (!src) return null;
  return <img src={src} alt="" className="w-full h-full object-cover" />;
}

function MediaVideoPreview({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    let revoke;
    axios.get(`${API}/files/${path}`, { responseType: 'blob', withCredentials: true })
      .then(r => { const url = URL.createObjectURL(r.data); revoke = url; setSrc(url); })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);
  if (!src) return null;
  return <video src={src} className="w-full h-full object-cover" autoPlay muted loop playsInline />;
}

// ==================== MESSAGES PANEL ====================
function MessagesPanel({ messages, clients, fetchMessages }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ subject: '', content: '', message_type: 'info' });
  const [selectedIds, setSelectedIds] = useState([]); // restaurant ids chosen
  const [broadcast, setBroadcast] = useState(false);
  const [sending, setSending] = useState(false);
  const [collapsed, setCollapsed] = useState({}); // { [restaurant_id]: true/false }

  const msgTypes = [
    { id: 'info', label: 'Info', icon: Info, color: 'bg-blue-100 text-blue-700' },
    { id: 'alert', label: 'Alert', icon: AlertTriangle, color: 'bg-orange-100 text-orange-700' },
    { id: 'feature', label: 'Feature Suggestion', icon: Lightbulb, color: 'bg-green-100 text-green-700' },
  ];

  const resetForm = () => {
    setForm({ subject: '', content: '', message_type: 'info' });
    setSelectedIds([]);
    setBroadcast(false);
  };

  const send = async () => {
    if (!form.subject || !form.content) { toast.error('Fill subject and message'); return; }
    if (!broadcast && selectedIds.length === 0) { toast.error('Select at least one restaurant'); return; }
    setSending(true);
    try {
      const payload = broadcast
        ? { broadcast: true, ...form }
        : { restaurant_ids: selectedIds, ...form };
      const { data } = await axios.post(`${API}/admin/messages`, payload, { withCredentials: true });
      toast.success(`Message sent to ${data.sent} restaurant${data.sent !== 1 ? 's' : ''}`);
      setDialogOpen(false);
      resetForm();
      fetchMessages();
    } catch { toast.error('Failed to send'); }
    setSending(false);
  };

  const getRestName = (rid) => clients.find(c => c.id === rid)?.name || 'Unknown';

  const toggleSelect = (rid) => {
    setSelectedIds(prev => prev.includes(rid) ? prev.filter(x => x !== rid) : [...prev, rid]);
  };

  const selectAll = () => setSelectedIds(clients.map(c => c.id));
  const clearAll = () => setSelectedIds([]);

  // Group messages by restaurant_id
  const grouped = {};
  messages.forEach(m => {
    if (!grouped[m.restaurant_id]) grouped[m.restaurant_id] = [];
    grouped[m.restaurant_id].push(m);
  });
  // Sort groups alphabetically by restaurant name
  const groupEntries = Object.entries(grouped).sort((a, b) =>
    getRestName(a[0]).localeCompare(getRestName(b[0]))
  );

  const toggleCollapse = (rid) => setCollapsed(prev => ({ ...prev, [rid]: !prev[rid] }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Messages</h2>
          <p className="text-sm text-stone-500 mt-1">Send alerts and suggestions to one, multiple, or all restaurants</p>
        </div>
        <Button data-testid="send-message-btn" onClick={() => setDialogOpen(true)} className="bg-stone-900 hover:bg-stone-800 text-white gap-2">
          <Send size={16} /> New Message
        </Button>
      </div>

      {messages.length === 0 && (
        <Card className="border-stone-200 border-dashed">
          <CardContent className="py-12 text-center">
            <Megaphone className="mx-auto text-stone-300 mb-3" size={40} />
            <p className="text-stone-500">No messages sent yet.</p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {groupEntries.map(([rid, groupMsgs]) => {
          const isCollapsed = collapsed[rid];
          const unread = groupMsgs.filter(m => !m.read).length;
          return (
            <Card key={rid} data-testid={`message-group-${rid}`} className="border-stone-200 overflow-hidden">
              <button
                onClick={() => toggleCollapse(rid)}
                className="w-full flex items-center justify-between px-4 py-3 bg-stone-50 hover:bg-stone-100 transition-colors"
                data-testid={`message-group-toggle-${rid}`}
              >
                <div className="flex items-center gap-3">
                  {isCollapsed ? <ChevronRight size={16} className="text-stone-500" /> : <ChevronDown size={16} className="text-stone-500" />}
                  <Store size={16} className="text-stone-600" />
                  <span className="font-semibold text-stone-900" style={{ fontFamily: 'Outfit' }}>{getRestName(rid)}</span>
                  <Badge variant="outline" className="text-[10px]">{groupMsgs.length}</Badge>
                  {unread > 0 && <Badge className="bg-orange-100 text-orange-700 text-[10px]">{unread} unread</Badge>}
                </div>
                <span className="text-xs text-stone-400">
                  Last: {new Date(groupMsgs[0].created_at).toLocaleDateString()}
                </span>
              </button>
              {!isCollapsed && (
                <div className="divide-y divide-stone-100">
                  {groupMsgs.map(m => {
                    const typeInfo = msgTypes.find(t => t.id === m.message_type) || msgTypes[0];
                    const TypeIcon = typeInfo.icon;
                    return (
                      <div key={m.id} className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${typeInfo.color}`}>
                            <TypeIcon size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold text-stone-900 text-sm">{m.subject}</p>
                              {m.read && <Badge className="bg-stone-100 text-stone-500 text-[10px]">Read</Badge>}
                            </div>
                            <p className="text-sm text-stone-600">{m.content}</p>
                            <p className="text-xs text-stone-400 mt-1">{new Date(m.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Send Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Send Message</DialogTitle>
            <p className="text-sm text-stone-500">Pick one, many, or broadcast to all restaurants</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Recipients {broadcast ? '(All)' : selectedIds.length > 0 ? `(${selectedIds.length} selected)` : ''}</Label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="toggle-broadcast-btn"
                    onClick={() => { setBroadcast(b => !b); setSelectedIds([]); }}
                    className={`text-xs px-2 py-1 rounded-md font-medium transition-colors ${broadcast ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                  >
                    {broadcast ? 'Broadcast ON' : 'Broadcast to all'}
                  </button>
                </div>
              </div>
              {!broadcast && (
                <div className="border border-stone-200 rounded-md">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-stone-100 bg-stone-50">
                    <button type="button" data-testid="select-all-restaurants-btn" onClick={selectAll} className="text-xs text-stone-700 hover:underline font-medium">Select all</button>
                    <button type="button" data-testid="clear-restaurants-btn" onClick={clearAll} className="text-xs text-stone-500 hover:underline">Clear</button>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {clients.length === 0 && (
                      <p className="text-sm text-stone-400 p-3">No restaurants available</p>
                    )}
                    {clients.map(c => (
                      <label key={c.id} className="flex items-center gap-2 px-3 py-2 hover:bg-stone-50 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`recipient-checkbox-${c.id}`}
                          checked={selectedIds.includes(c.id)}
                          onChange={() => toggleSelect(c.id)}
                          className="w-4 h-4 rounded border-stone-300 text-stone-900 focus:ring-stone-500"
                        />
                        <span className="text-sm text-stone-800">{c.name}</span>
                        <span className="text-xs text-stone-400">{c.owner?.email}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {broadcast && (
                <div className="px-3 py-2 bg-orange-50 border border-orange-200 rounded-md text-xs text-orange-800">
                  This message will be sent to all {clients.length} restaurants.
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <div className="flex gap-2">
                {msgTypes.map(t => (
                  <button key={t.id} onClick={() => setForm(p => ({ ...p, message_type: t.id }))}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all ${
                      form.message_type === t.id ? 'border-stone-900' : 'border-stone-200'
                    } ${t.color}`}>
                    <t.icon size={14} /> {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input data-testid="message-subject-input" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="e.g. New Feature Available" />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea data-testid="message-content-input" value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} rows={3} placeholder="Write your message..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="send-message-confirm-btn" onClick={send} disabled={sending} className="bg-stone-900 hover:bg-stone-800 text-white gap-1">
              <Send size={14} /> {sending ? 'Sending...' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ==================== CUSTOMERS PANEL ====================
function CustomersPanel({ settings, setSettings }) {
  const [customers, setCustomers] = useState([]);
  const [size, setSize] = useState(120);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setCustomers(Array.isArray(settings?.customers) ? settings.customers : []);
    setSize(settings?.customers_image_size_px || 120);
  }, [settings]);

  const save = async (nextCustomers = customers, nextSize = size) => {
    try {
      setSaving(true);
      const { data } = await axios.put(`${API}/admin/site-settings`,
        { customers: nextCustomers, customers_image_size_px: nextSize },
        { withCredentials: true });
      setSettings(data);
      toast.success('Customers saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const next = [...customers, { id: crypto.randomUUID(), image_path: data.path, country: '' }];
      setCustomers(next);
      save(next, size);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const updateCountry = (id, country) => {
    setCustomers(prev => prev.map(c => c.id === id ? { ...c, country } : c));
  };

  const commitCountry = () => save(customers, size);

  const removeItem = (id) => {
    const next = customers.filter(c => c.id !== id);
    setCustomers(next);
    save(next, size);
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Customers</h2>
          <p className="text-sm text-stone-500 mt-1">Upload customer photos with country labels — displayed as a 12-column grid on the landing page</p>
        </div>
      </div>

      <Card className="border-stone-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Outfit' }}>Grid Image Size</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-xs text-stone-500">Size: {size}px square</Label>
            <input
              data-testid="customers-size-slider"
              type="range" min={60} max={280} step={4}
              value={size}
              onChange={e => setSize(parseInt(e.target.value))}
              onMouseUp={() => save(customers, size)}
              onTouchEnd={() => save(customers, size)}
              className="flex-1 max-w-md accent-orange-600"
            />
            <span className="text-xs text-stone-400">min 60 · max 280</span>
          </div>
        </CardContent>
      </Card>

      <Card className="border-stone-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Outfit' }}>Customer Photos ({customers.length})</CardTitle>
          <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium cursor-pointer transition-colors">
            <Upload size={14} />
            <span>{uploading ? 'Uploading…' : 'Add Photo'}</span>
            <input data-testid="customer-upload-input" type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
        </CardHeader>
        <CardContent>
          {customers.length === 0 ? (
            <div className="py-12 text-center border-2 border-dashed border-stone-200 rounded-xl">
              <ImageIcon className="mx-auto text-stone-300 mb-2" size={40} />
              <p className="text-sm text-stone-500">No customer photos yet. Upload your first one.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {customers.map(c => (
                <CustomerCard key={c.id} customer={c} onCountryChange={(val) => updateCountry(c.id, val)} onCountryCommit={commitCountry} onRemove={() => removeItem(c.id)} />
              ))}
            </div>
          )}
          {saving && <p className="text-xs text-stone-400 mt-3 text-center">Saving…</p>}
        </CardContent>
      </Card>
    </div>
  );
}

function CustomerCard({ customer, onCountryChange, onCountryCommit, onRemove }) {
  const [imgSrc, setImgSrc] = useState(null);
  useEffect(() => {
    if (!customer.image_path) return;
    let revoke;
    axios.get(`${API}/files/${customer.image_path}`, { responseType: 'blob', withCredentials: true })
      .then(r => { const url = URL.createObjectURL(r.data); revoke = url; setImgSrc(url); })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [customer.image_path]);
  return (
    <div data-testid={`customer-card-${customer.id}`} className="group relative rounded-xl border border-stone-200 overflow-hidden bg-white">
      <div className="aspect-square bg-stone-100 relative">
        {imgSrc ? <img src={imgSrc} alt={customer.country || 'Customer'} className="absolute inset-0 w-full h-full object-cover" /> :
          <div className="absolute inset-0 flex items-center justify-center text-stone-300"><ImageIcon size={28} /></div>
        }
        <button data-testid={`customer-remove-${customer.id}`} onClick={onRemove} className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50" title="Remove">
          <Trash2 size={14} />
        </button>
      </div>
      <div className="p-2">
        <Input
          data-testid={`customer-country-${customer.id}`}
          value={customer.country || ''}
          onChange={e => onCountryChange(e.target.value)}
          onBlur={onCountryCommit}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
          placeholder="Country"
          className="text-xs h-8"
        />
      </div>
    </div>
  );
}

// ==================== HOW IT WORKS PANEL ====================
const BENTO_SIZES = [
  { id: 'sm', label: 'Small · 1×1' },
  { id: 'md', label: 'Wide · 2×1' },
  { id: 'tall', label: 'Tall · 1×2' },
  { id: 'lg', label: 'Large · 2×2' },
  { id: 'wide', label: 'Extra Wide · 3×1' },
];

function HowItWorksPanel({ settings, setSettings }) {
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);

  useEffect(() => {
    setItems(Array.isArray(settings?.how_it_works_items) ? settings.how_it_works_items : []);
    setEnabled(settings?.how_it_works_enabled !== false);
  }, [settings]);

  const persist = async (patch) => {
    try {
      setSaving(true);
      const { data } = await axios.put(`${API}/admin/site-settings`, patch, { withCredentials: true });
      setSettings(data);
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const persistItems = (next) => persist({ how_it_works_items: next });
  const toggleEnabled = async (val) => {
    setEnabled(val);
    await persist({ how_it_works_enabled: val });
    toast.success(val ? '"How It Works" page is live' : '"How It Works" page hidden');
  };

  const addItem = () => {
    const next = [...items, { id: crypto.randomUUID(), title: '', description: '', image_path: '', size: 'sm', image_fit: 'cover' }];
    setItems(next);
    persistItems(next);
  };

  const updateItem = (id, patch) => {
    setItems(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it));
  };

  const commitItem = () => persistItems(items);

  const removeItem = (id) => {
    const next = items.filter(it => it.id !== id);
    setItems(next);
    persistItems(next);
  };

  const moveItem = (id, delta) => {
    const idx = items.findIndex(i => i.id === id);
    if (idx < 0) return;
    const ni = idx + delta;
    if (ni < 0 || ni >= items.length) return;
    const next = items.slice();
    [next[idx], next[ni]] = [next[ni], next[idx]];
    setItems(next);
    persistItems(next);
  };

  const handleUpload = async (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const next = items.map(it => it.id === id ? { ...it, image_path: data.path } : it);
      setItems(next);
      persistItems(next);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(null); e.target.value = ''; }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>How It Works</h2>
          <p className="text-sm text-stone-500 mt-1">Bento-style cards shown on the public /how-it-works page.</p>
        </div>
        <Button data-testid="how-add-btn" onClick={addItem} className="bg-orange-600 hover:bg-orange-700 text-white">
          <Plus size={14} className="mr-2" /> Add Card
        </Button>
      </div>

      {/* Page enable toggle */}
      <Card className="border-stone-200">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="flex-1">
            <p className="text-sm font-semibold text-stone-800">Show the "How It Works" page</p>
            <p className="text-xs text-stone-500">When off, the page is hidden from the public site and the nav link disappears.</p>
          </div>
          <Switch data-testid="how-enabled-toggle" checked={enabled} onCheckedChange={toggleEnabled} />
        </CardContent>
      </Card>

      {items.length === 0 && (
        <Card className="border-dashed border-stone-200">
          <CardContent className="py-12 text-center text-stone-400 text-sm">No cards yet — click <strong>Add Card</strong> to create your first one.</CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {items.map((item, idx) => (
          <Card key={item.id} data-testid={`how-card-${item.id}`} className="border-stone-200">
            <CardContent className="p-4">
              <div className="grid md:grid-cols-[160px,1fr,auto] gap-4 items-start">
                {/* Image */}
                <div className="space-y-2">
                  <div className="aspect-video rounded-lg bg-stone-100 border border-stone-200 overflow-hidden relative">
                    {item.image_path ? <HowImagePreview path={item.image_path} /> :
                      <div className="absolute inset-0 flex items-center justify-center text-stone-300"><ImageIcon size={28} /></div>}
                  </div>
                  <label className="block">
                    <input data-testid={`how-upload-${item.id}`} type="file" accept="image/*" className="hidden" disabled={uploading === item.id} onChange={(e) => handleUpload(item.id, e)} />
                    <span className="inline-flex items-center justify-center gap-1.5 w-full px-2 py-1.5 rounded-md border border-stone-300 text-xs text-stone-600 hover:bg-stone-50 cursor-pointer">
                      <Upload size={12} /> {uploading === item.id ? 'Uploading…' : (item.image_path ? 'Replace' : 'Upload')}
                    </span>
                  </label>
                </div>
                {/* Text */}
                <div className="space-y-2">
                  <Input data-testid={`how-title-${item.id}`} placeholder="Card title" value={item.title || ''} onChange={e => updateItem(item.id, { title: e.target.value })} onBlur={commitItem} className="font-semibold" />
                  <Textarea data-testid={`how-desc-${item.id}`} placeholder="Short description shown on the card" value={item.description || ''} onChange={e => updateItem(item.id, { description: e.target.value })} onBlur={commitItem} rows={3} />
                  <div className="flex items-center gap-2 flex-wrap">
                    <Label className="text-xs text-stone-500">Size:</Label>
                    {BENTO_SIZES.map(sz => (
                      <button
                        key={sz.id}
                        data-testid={`how-size-${item.id}-${sz.id}`}
                        type="button"
                        onClick={() => { updateItem(item.id, { size: sz.id }); setTimeout(commitItem, 0); }}
                        className={`px-2 py-1 rounded-md text-xs font-medium transition ${item.size === sz.id ? 'bg-orange-100 text-orange-700 border border-orange-300' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                      >
                        {sz.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Label className="text-xs text-stone-500">Image fit:</Label>
                    {[
                      { id: 'cover', label: 'Fill (crop)' },
                      { id: 'contain', label: 'Fit (whole image)' },
                    ].map(f => (
                      <button
                        key={f.id}
                        data-testid={`how-fit-${item.id}-${f.id}`}
                        type="button"
                        onClick={() => { updateItem(item.id, { image_fit: f.id }); setTimeout(commitItem, 0); }}
                        className={`px-2 py-1 rounded-md text-xs font-medium transition ${(item.image_fit || 'cover') === f.id ? 'bg-orange-100 text-orange-700 border border-orange-300' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                  {/* Text-box background color + opacity + text color */}
                  <div className="grid grid-cols-[auto,1fr,auto,auto] items-center gap-2 pt-1">
                    <Label className="text-xs text-stone-500">Text bg:</Label>
                    <div className="flex items-center gap-2">
                      <input
                        data-testid={`how-overlay-color-${item.id}`}
                        type="color"
                        value={item.overlay_color || '#000000'}
                        onChange={e => updateItem(item.id, { overlay_color: e.target.value })}
                        onBlur={commitItem}
                        className="w-8 h-8 rounded border border-stone-300 cursor-pointer"
                      />
                      <input
                        data-testid={`how-overlay-opacity-${item.id}`}
                        type="range" min={0} max={100}
                        value={typeof item.overlay_opacity === 'number' ? item.overlay_opacity : 65}
                        onChange={e => updateItem(item.id, { overlay_opacity: parseInt(e.target.value) })}
                        onMouseUp={commitItem}
                        onTouchEnd={commitItem}
                        className="flex-1 accent-orange-600"
                      />
                      <span className="text-xs text-stone-400 w-9 text-right">{typeof item.overlay_opacity === 'number' ? item.overlay_opacity : 65}%</span>
                    </div>
                    <Label className="text-xs text-stone-500">Text:</Label>
                    <input
                      data-testid={`how-text-color-${item.id}`}
                      type="color"
                      value={item.text_color || '#ffffff'}
                      onChange={e => updateItem(item.id, { text_color: e.target.value })}
                      onBlur={commitItem}
                      className="w-8 h-8 rounded border border-stone-300 cursor-pointer"
                    />
                  </div>
                </div>
                {/* Actions */}
                <div className="flex md:flex-col gap-1">
                  <Button data-testid={`how-up-${item.id}`} variant="ghost" size="icon" onClick={() => moveItem(item.id, -1)} disabled={idx === 0} className="h-8 w-8"><ChevronRight size={14} className="-rotate-90" /></Button>
                  <Button data-testid={`how-down-${item.id}`} variant="ghost" size="icon" onClick={() => moveItem(item.id, 1)} disabled={idx === items.length - 1} className="h-8 w-8"><ChevronRight size={14} className="rotate-90" /></Button>
                  <Button data-testid={`how-del-${item.id}`} variant="ghost" size="icon" onClick={() => removeItem(item.id)} className="h-8 w-8 text-red-600 hover:bg-red-50"><Trash2 size={14} /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {saving && <p className="text-xs text-stone-400 text-center">Saving…</p>}
    </div>
  );
}

function HowImagePreview({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    let revoke;
    axios.get(`${API}/files/${path}`, { responseType: 'blob', withCredentials: true })
      .then(r => { const url = URL.createObjectURL(r.data); revoke = url; setSrc(url); })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);
  if (!src) return <div className="absolute inset-0 flex items-center justify-center text-stone-300"><ImageIcon size={28} /></div>;
  return <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />;
}

